import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../../shared/components/toast-notification/toast.service';
import { SKIP_SESSION_INVALIDATION } from './http-context.tokens';

/**
 * Global error interceptor (functional, Angular 17+).
 * Catches HTTP errors and shows user-facing toast notifications.
 * - 400: Extracts backend message(s)
 * - 401: Logs out + redirects to login
 * - 403: Forbidden toast
 * - 500+: Internal server error toast
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const toastService = inject(ToastService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            // Skip toast for login failures (handled directly in LoginComponent)
            const isLoginRequest = req.url.includes('/auth/login');

            switch (error.status) {
                case 400:
                    if (!isLoginRequest) {
                        const msg = extractErrorMessage(error);
                        toastService.error(msg);
                    }
                    break;

                case 401:
                    if (!isLoginRequest && !req.context.get(SKIP_SESSION_INVALIDATION)) {
                        toastService.warning('Tu sesión ha expirado. Inicia sesión nuevamente.');
                        authService.invalidateSession();
                    }
                    break;

                case 0:
                    if (!isLoginRequest && !req.context.get(SKIP_SESSION_INVALIDATION)) {
                        toastService.error('No se pudo conectar con el backend. Inicia sesión nuevamente cuando el servicio esté disponible.');
                        authService.invalidateSession();
                    }
                    break;

                case 403:
                    toastService.error('No tienes permisos para realizar esta acción.');
                    break;

                case 404:
                    // 404s are usually handled locally, only show toast if not
                    break;

                case 409:
                    if (req.url.includes('/passenger')) {
                        toastService.error('Ya existe un pasajero con ese tipo y número de documento. Verifica los datos o utiliza el pasajero existente.');
                    } else {
                        toastService.error(extractErrorMessage(error));
                    }
                    break;

                case 422:
                    toastService.error(extractErrorMessage(error));
                    break;

                default:
                    if (error.status >= 500) {
                        toastService.error('Error interno del servidor. Intenta de nuevo más tarde.');
                    }
                    break;
            }

            return throwError(() => error);
        })
    );
};

/**
 * Extracts a user-friendly message from the NestJS error response.
 * Handles both `string` and `string[]` formats for `message`.
 */
function extractErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;
    if (body?.message) {
        if (Array.isArray(body.message)) {
            return body.message.join('. ');
        }
        return String(body.message);
    }
    return 'Solicitud inválida. Revisa los datos ingresados.';
}
